"use client";

import {
  evaluateFlow,
  isAnswered,
  isFieldRequired,
  translationSourceHash,
  SPANISH_CHROME,
  visibleFields,
  type QuestionnaireAnswers,
} from "@repo/questionnaire";
import { useEffect, useMemo, useRef, useState } from "react";

import { isQuestionnaireReviewStep } from "./questionnaire-review";

import type { QuestionnaireRunnerProps } from "./questionnaire-runner-types";
import { mergeAuthoritativeAnswers } from "./questionnaire-runner-values";
const EMPTY_QUESTIONNAIRE_ANSWERS: QuestionnaireAnswers = {};
const AUTOSAVE_MS = 500;
export function useQuestionnaireState({
  definition,
  answers: controlledAnswers,
  initialAnswers = EMPTY_QUESTIONNAIRE_ANSWERS,
  currentStepId,
  jumpToStepId,
  onAnswersChange,
  onStepChange,
  onSaveStep,
  previewMode = false,
  locale = "en",
  chromeCache = {},
}: QuestionnaireRunnerProps) {
  const [internalAnswers, setInternalAnswers] =
    useState<QuestionnaireAnswers>(initialAnswers);
  const answers = controlledAnswers ?? internalAnswers;
  const answersRef = useRef(answers);
  answersRef.current = answers;
  const [activeStepId, setActiveStepId] = useState<string | null>(
    currentStepId ?? null,
  );
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [actionError, setActionError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const dirtyRef = useRef(false);
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const returnToReviewRef = useRef(false);
  const fieldsContainerRef = useRef<HTMLDivElement>(null);
  const handleContinueRef = useRef<() => Promise<void>>(async () => {});

  useEffect(() => {
    if (currentStepId) setActiveStepId(currentStepId);
  }, [currentStepId]);

  useEffect(() => {
    if (jumpToStepId) setActiveStepId(jumpToStepId);
  }, [jumpToStepId]);

  useEffect(() => {
    if (controlledAnswers == null) setInternalAnswers(initialAnswers);
  }, [controlledAnswers, initialAnswers]);

  const flow = useMemo(
    () =>
      evaluateFlow({
        definition,
        answers,
        currentStepId: activeStepId,
      }),
    [activeStepId, answers, definition],
  );

  useEffect(() => {
    if (flow.currentStep.id !== activeStepId) {
      setActiveStepId(flow.currentStep.id);
    }
  }, [activeStepId, flow.currentStep.id]);

  const currentStepIdRef = useRef(flow.currentStep.id);
  currentStepIdRef.current = flow.currentStep.id;
  const currentFields = visibleFields(flow.currentStep, answers);
  const pageField =
    currentFields.length === 1 && currentFields[0]?.kind === "page"
      ? currentFields[0]
      : null;
  const isStartPage = pageField?.placement === "start";
  const reviewStep = isQuestionnaireReviewStep(flow.currentStep);
  const requiredComplete = reviewStep
    ? true
    : currentFields.every(
        (field) =>
          !isFieldRequired(field, answers) || isAnswered(field, answers),
      );
  const currentStepIndex = Math.max(
    0,
    flow.visibleSteps.findIndex((step) => step.id === flow.currentStep.id),
  );
  const reviewStepId =
    flow.visibleSteps.find((step) => isQuestionnaireReviewStep(step))?.id ??
    null;

  const handleEditSection = async (stepId: string) => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    if (dirtyRef.current) {
      await persistDraft(flow.currentStep.id);
    }
    returnToReviewRef.current = true;
    moveToStep(stepId);
  };

  const updateAnswers = (next: QuestionnaireAnswers) => {
    if (controlledAnswers == null) setInternalAnswers(next);
    onAnswersChange?.(next);
  };

  const applyServerAnswers = (server?: QuestionnaireAnswers) => {
    const merged = mergeAuthoritativeAnswers(answersRef.current, server);
    updateAnswers(merged);
    return merged;
  };

  const persistDraft = async (stepId: string) => {
    if (previewMode || !onSaveStep || !dirtyRef.current) return;
    try {
      const result = await onSaveStep({
        definitionId: definition.id,
        definitionVersion: definition.version,
        stepId,
        answers: answersRef.current,
        stepAnswers: answersRef.current,
        nextStepId: stepId,
        mode: "draft",
      });
      dirtyRef.current = false;
      if (result?.answers) applyServerAnswers(result.answers);
    } catch {
      // Keep dirty so the next Continue / Back can retry.
    }
  };

  const scheduleAutosave = (stepId: string) => {
    dirtyRef.current = true;
    if (previewMode || !onSaveStep) return;
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => {
      void persistDraft(stepId);
    }, AUTOSAVE_MS);
  };

  useEffect(() => {
    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    };
  }, []);

  const moveToStep = (stepId: string) => {
    setFieldErrors({});
    setActionError(null);
    setActiveStepId(stepId);
    onStepChange?.(stepId);
  };

  const handleBack = async () => {
    if (!flow.previousStep) return;
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    if (dirtyRef.current) {
      await persistDraft(flow.currentStep.id);
    }
    moveToStep(flow.previousStep.id);
  };

  const chromeLabel = (english: string) => {
    if (locale === "en") return english;
    return (
      chromeCache[translationSourceHash(english)] ??
      (locale === "es" ? SPANISH_CHROME[english] : undefined) ??
      english
    );
  };
  return {
    flow,
    answers,
    answersRef,
    dirtyRef,
    autosaveTimerRef,
    returnToReviewRef,
    fieldsContainerRef,
    handleContinueRef,
    currentStepIdRef,
    fieldErrors,
    setFieldErrors,
    actionError,
    setActionError,
    saving,
    setSaving,
    submitted,
    setSubmitted,
    currentFields,
    pageField,
    isStartPage,
    reviewStep,
    requiredComplete,
    currentStepIndex,
    reviewStepId,
    handleEditSection,
    updateAnswers,
    applyServerAnswers,
    persistDraft,
    scheduleAutosave,
    moveToStep,
    handleBack,
    chromeLabel,
  };
}
export type QuestionnaireState = ReturnType<typeof useQuestionnaireState>;
